import React, { useState, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import TaskNav from "components/task-nav/task-nav";
import HeaderUpc from "components/h1upc";
import NoteLauncher from "components/notes";
import NotificationBar from "components/notification-bar";
import QANav from "components/task-nav/qa-nav";
import Loader from "components/loader/loader";
import * as notify from "shared/notify";
import { uniq } from "lodash";
import { SidePanel } from "components/side-panel/side-panel";
import { ToggleSwitch } from "components/form-inputs/custom/toggle-switch";
import { TaskContext } from "contexts/task";
import { SourcePage } from "shared/taskTypeMap";
import { ReferenceImages } from "../../shared/components/reference-images/reference-images";
import { useAllBVAttributeGroups } from "./hooks/use-all-bv-attribute-groups";
import CompareHeader from "components/attributes/compare-table/compare-header";
import { BvQaAttributeTable } from "./bv-qa-attribute-table";
import "./brand-verified.scss";
import { RejectToSupplier } from "components/photography/components/reject-to-supplier";
import { validateTask } from "shared/taskState";
import { BRAND_VERIFIED_WORKFLOW_TYPE_ID } from "components/shipment-editor/lib/constants";
import { useAggregatedProductInfo } from "shared/hooks/api/use-aggregated-product-info";

const rejectOptions = {
    rejectSupplier: "Reject Supplier",
    rejectInternal: "Reject Internal",
};

const BrandVerifiedQAIndex = () => {
    const navigate = useNavigate();
    const { task } = useContext(TaskContext);
    const { data, isSuccess, isFetched, refetch: refreshAttributeGroups } = useAllBVAttributeGroups(task.key);
    const [columnWidths, setColumnWidth] = useState({});
    const [viewAllAttributes, setViewAllAttributes] = useState(false);
    const [attrList, setAttrList] = useState(new Map());
    const disabled = [...attrList.values()].some((attr) => !!attr.open);
    const { data: productInfo } = useAggregatedProductInfo(task.context.shipmentProductKey);
    const isPhotographyOpen = productInfo?.areAllPhotographyTasksCompleted === false;
    const hasOpenRedoTasks = task.stateDescription === "Open" || isPhotographyOpen;

    const handlePromptMessages = (status, taskRejects) => {
        if (status === rejectOptions.rejectSupplier) {
            return "You have rejected attribute(s) that will be sent back to the supplier.";
        }

        if (status === rejectOptions.rejectInternal) {
            const parentTasks = [...taskRejects.values()].map((value) => value.parentTask);

            return `You have rejected attribute(s) that will be sent back to ${uniq(parentTasks).join(", ")} task(s) in Intake.`;
        }

        return "You have accepted all brand data.";
    };

    const handleTaskStatus = () => {
        const isSupplierAttributeRejected = [...attrList.values()].some((attr) => attr.isSupplierAttribute);

        if (isSupplierAttributeRejected) {
            return handlePromptMessages(rejectOptions.rejectSupplier);
        }

        if (!isSupplierAttributeRejected && attrList.size > 0) {
            return handlePromptMessages(rejectOptions.rejectInternal, attrList);
        }

        return handlePromptMessages();
    };

    const validateProduct = async (override = false) => {
        const confirm = await notify.confirm(`${handleTaskStatus()} Are you sure you want to continue? This action cannot be undone.`);

        if (!confirm) {
            return false;
        }

        const serverValidate = await validateTask(task.key, override, { passthrough: true, displayError: true });

        if (serverValidate.errors && serverValidate.errors.length > 0) {
            return false;
        }

        if (!override && serverValidate.overridableErrors && serverValidate.overridableErrors.length > 0) {
            const displayErrors = serverValidate.overridableErrors.join(", ");
            const confirmOverride = await notify.confirm(`${displayErrors}`);

            if (confirmOverride) {
                return validateProduct(true);
            } else {
                return false;
            }
        }

        return true;
    };

    const onComplete = () => {
        navigate("/task-list/brandverifiedqa");
    };

    const handleColumnWidthChange = useCallback((col, width) => {
        setColumnWidth((state) => ({ ...state, [col]: width }));
    }, []);

    const handleInitialColSizes = useCallback(() => setColumnWidth({}), []);

    if (!isFetched || !isSuccess) {
        return (
            <div className="txt-center">
                <Loader natural={true} />
            </div>
        );
    }

    return (
        <div className="flex">
            <SidePanel />
            <div className="container flex-1">
                <TaskNav
                    taskKey={task.key}
                    hasIMDropboxContent={task.hasIMDropboxContent}
                    isSampleProduct={task.isSampleUpc}
                    fixAfter={250}
                >
                    <div className="button-group">
                        <div className="m-l-14 header-title">
                            <HeaderUpc
                                isBrandVerified
                                productData={{ ...data.roles?.[1]?.values, ...data.roles?.[0]?.values }}
                            />
                        </div>
                        <div className="button-group -right -margin-right">
                            <div className="flex justify-content-end m-r-16">
                                <ToggleSwitch
                                    variant="small"
                                    name="viewAllAttributes"
                                    value={viewAllAttributes}
                                    updateOnChange={(name, value) => setViewAllAttributes(value)}
                                />
                                <div className="m-l-4">View All Attributes</div>
                            </div>
                            <RejectToSupplier
                                wipKey={task.context.photographyWipKey}
                                onComplete={onComplete}
                                disabled={task.isCompleted || hasOpenRedoTasks}
                                workflowType={BRAND_VERIFIED_WORKFLOW_TYPE_ID}
                            />
                            <NoteLauncher
                                settings={{
                                    shipmentKey: task.context.shipmentKey,
                                    shipmentProductKey: task.context.shipmentProductKey,
                                }}
                                clientCode={task.context.cxHClientCode}
                                binTrackingNumber={[task.context.binTrackingNumber]}
                                noteType="Product"
                                taskType="brandverifiedqa.standard"
                                sourcePage={SourcePage[task.taskType]}
                                isReadOnly={false}
                            />
                            {!task.isCompleted && (
                                <QANav
                                    taskId={task.key}
                                    onComplete={onComplete}
                                    validate={validateProduct}
                                    isQA={false}
                                    hideReject={true}
                                    taskType="brandverifiedqa.standard"
                                    sourcePage={SourcePage[task.taskType]}
                                    isDirty={disabled || hasOpenRedoTasks}
                                />
                            )}
                        </div>
                    </div>
                </TaskNav>
                <div className="m-l-30">
                    <NotificationBar
                        className="m-l-32"
                        variant="small"
                        type="info"
                    >
                        <span className="txt-bold">Brand Verified</span>
                    </NotificationBar>
                </div>
                <div className="bv-qa-table">
                    <CompareHeader
                        isQa={true}
                        columnWidths={columnWidths}
                        referenceProducts={[]}
                        onColumnWidthChange={handleColumnWidthChange}
                        onInitColSizes={handleInitialColSizes}
                        roleLabels={data.roles}
                        hideSelect={true}
                        styleOverride="large-header"
                    />
                    <BvQaAttributeTable
                        viewAllAttributes={viewAllAttributes}
                        columnWidths={columnWidths}
                        attributeGroups={data.groups}
                        roleLabels={data.roles}
                        codingWipKey={task.context.codingWipKey}
                        onAttrList={setAttrList}
                        refreshAttributeGroups={refreshAttributeGroups}
                        isCompleted={task.isCompleted || hasOpenRedoTasks}
                    />
                </div>
                <ReferenceImages
                    photographyWipKey={task.context.photographyWipKey}
                    shipmentKey={task.context.shipmentKey}
                    shipmentProductKey={task.context.shipmentProductKey}
                />
            </div>
        </div>
    );
};

export default BrandVerifiedQAIndex;
